import { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { RegItem } from 'src/types/regItem'
import { showCartItemAddedToast } from 'shared/CartItemAddedToast/showCartItemAddedToast'
import { UPDATE_CART_SUMMARY } from 'shared/constants'
import { track } from 'lib/analytics'
import { CartItemsAPI } from 'src/routes/(shop)/api/cartItems'
import { optimisticallyUpdateRegItem } from './registryHelpers'
import { showCartQuantityUpdateSuccessToast } from '../components/CartQuantityUpdateSuccessToast/showCartQuantityUpdateSuccessToast'

interface CartResponse {
  cart: {
    items: Array<{ uuid: string; quantity: number; regItem: RegItem }>
    checkoutTotals?: {
      itemsSubtotal: number
    }
  }
}

interface UseCartItemManagerProps {
  regItem: RegItem
  imageOverride?: ReactNode
  unredeemedFundsCallback: () => void
  trackingEventLocation: string
}
export const useCartItemManager = ({
  regItem,
  imageOverride,
  unredeemedFundsCallback,
  trackingEventLocation,
}: UseCartItemManagerProps) => {
  const queryClient = useQueryClient()
  const createCartItemMutation = CartItemsAPI.useCreateMutation()
  const updateCartItemMutation = CartItemsAPI.useUpdateMutation({
    flashMessageEnabled: false,
  })
  const deleteCartItemMutation = CartItemsAPI.useDeleteMutation()

  const addToCart = () =>
    createCartItemMutation.mutateAsync(
      {
        productId: regItem.productId,
        quantity: 1,
        regItemId: regItem.id,
      },
      {
        onSuccess: (response: CartResponse) => {
          const cartItem = response.cart.items.find(
            (item) => item.regItem?.id === regItem.id
          )
          const cartItemId = parseInt(cartItem?.uuid || '')

          const images = [{ url: regItem.imgUrl, thumbnailUrl: '' }]
          showCartItemAddedToast({
            product: {
              name: regItem.title,
              images,
            },
            image: imageOverride,
            timeout: 2000,
          })
          if (
            regItem.fundsUnredeemedAmount &&
            regItem.fundsUnredeemedAmount > 0
          ) {
            unredeemedFundsCallback()
          }

          const updatedRegItem = {
            ...regItem,
            cartInfo: {
              cartItemId,
              quantity: cartItem?.quantity,
            },
          }
          optimisticallyUpdateRegItem({
            queryClient,
            updatedRegItem,
          })

          PubSub.publish(UPDATE_CART_SUMMARY, { animateBadge: true })

          track.addedToCart({
            cartItemQuantity: cartItem?.quantity || 1,
            eventLocation: trackingEventLocation,
            storeAdded: track.StoreAdded.BABYLIST,
            cartTypes: [track.CartTypes.STORE],
            cartStores: [track.CartStores.BABYLIST_STORE],
            cartTotalValue: response.cart.checkoutTotals?.itemsSubtotal,
            productPrice: regItem.price,
            productType: track.ProductType.PRODUCT,
            subscribeToMarketingEmail: false,
            registryId: regItem.registryId,
            productIds: [regItem.productId],
            regItemIds: [regItem.id],
          })
        },
      }
    )

  const updateCartItemQuantity = (quantity: number): Promise<any> => {
    const cartItemId = regItem.cartInfo?.cartItemId
    if (!cartItemId) return Promise.reject('No matching cart item found')

    return updateCartItemMutation.mutateAsync(
      { id: cartItemId, quantity },
      {
        onSuccess: (response: CartResponse) => {
          const cartItem = response.cart.items.find(
            (item) => item.regItem?.id === regItem.id
          )

          showCartQuantityUpdateSuccessToast(quantity)

          const updatedRegItem = {
            ...regItem,
            cartInfo: {
              cartItemId,
              quantity: cartItem?.quantity || 0,
            },
          }
          optimisticallyUpdateRegItem({
            queryClient,
            updatedRegItem,
          })

          PubSub.publish(UPDATE_CART_SUMMARY, { animateBadge: true })

          return updatedRegItem
        },
      }
    )
  }

  const removeCartItem = (): Promise<any> => {
    const cartItemId = regItem.cartInfo?.cartItemId
    if (!cartItemId) return Promise.reject('No matching cart item found')

    return deleteCartItemMutation.mutateAsync(cartItemId.toString(), {
      onSuccess: () => {
        showCartQuantityUpdateSuccessToast(0)

        const updatedRegItem = {
          ...regItem,
          cartInfo: null,
        }
        optimisticallyUpdateRegItem({
          queryClient,
          updatedRegItem,
        })

        PubSub.publish(UPDATE_CART_SUMMARY, { animateBadge: true })

        return updatedRegItem
      },
    })
  }

  return {
    addToCart,
    updateCartItemQuantity,
    removeCartItem,
  }
}
